import { PatientPayload, UpdateGuardianPayload } from '_api/types';
import { ACCOUNT_TYPE } from '_constants';
import {
  LocalizedDatePicker,
  InputField,
  SelectMultipleField,
  SelectField,
  TextDivider,
} from 'components/molecules/formFields';
import { FormBuilder } from 'components/organisms/forms/form-builder';
import React, { useMemo, useState } from 'react';
import { usePatientForm, useTherapists, useUser } from 'utils/hooks';

import { EditPatientFormProps, EditPatientFormType } from './types';

export const EditPatientForm = ({
  disableAll = false,
  initValue,
  onBack,
  onSubmit,
}: EditPatientFormProps) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fullImageUrl,
    diagnosis,
    ethnicity,
    gender,
    guardian,
    race,
    sex,
    therapists,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    status,
    ...patient
  } = initValue;
  const { diagnosesOptions, ethnicityOptions, genderOptions, raceOptions, sexOptions } =
    usePatientForm();
  const { isLoading, therapistOptions } = useTherapists();
  const { account } = useUser();
  const isTherapistOrGuardian = useMemo(
    () =>
      account?.accountType === ACCOUNT_TYPE.THERAPIST ||
      account?.accountType === ACCOUNT_TYPE.GUARDIAN,
    [account],
  );

  const [updatedPatient, setUpdatedPatient] = useState<PatientPayload>({
    ...patient,
    diagnosis: diagnosis.map((d) => d.name),
    ethnicity: ethnicity ? ethnicity.name : '',
    gender: gender ? gender.name : '',
    race: race ? race.name : '',
    sex: sex ? sex.name : '',
    therapistIds: therapists.map(({ id }) => `${id}`),
  });
  const [updatedGuardian, setUpdatedGuardian] = useState<UpdateGuardianPayload>({
    id: guardian.id,
    firstName: guardian.firstName,
    lastName: guardian.lastName,
    email: guardian.email,
    phoneNumber: guardian.phoneNumber,
    imageUrl: guardian.imageUrl,
  });
  const formValue: EditPatientFormType = useMemo(
    () => ({
      ...updatedPatient,
      guardianfirstName: updatedGuardian.firstName,
      guardianlastName: updatedGuardian.lastName,
      guardianemail: updatedGuardian.email,
      guardianphoneNumber: updatedGuardian.phoneNumber,
    }),
    [updatedPatient, updatedGuardian],
  );

  const updateForm = async (key: any, value: any) => {
    if (key.includes('guardian')) {
      const guardianKey = key.replace('guardian', '');
      setUpdatedGuardian((current) => ({
        ...current,
        [guardianKey]: value,
      }));
    } else {
      setUpdatedPatient((current) => ({
        ...current,
        [key]: value,
      }));
    }
  };

  const generalFormFields = [
    {
      component: TextDivider,
      label: 'Child Details',
      fieldKey: 'childDetails-text',
      xs: 12,
      gridItemCss: { marginTop: '10px' },
    },
    {
      component: InputField,
      fieldKey: 'firstName',
      label: 'FIRST NAME',
      required: true,
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'lastName',
      label: 'LAST NAME',
      required: true,
      xs: 3,
    },
    {
      component: LocalizedDatePicker,
      fieldKey: 'dateOfBirth',
      label: 'DATE OF BIRTH',
      required: true,
      xs: 3,
    },
    {
      component: InputField,
      fieldKey: 'mrn',
      label: 'MRN',
      required: false,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'sex',
      label: 'SEX',
      options: sexOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'gender',
      label: 'GENDER IDENTITY',
      options: genderOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'race',
      label: 'RACE',
      options: raceOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectField,
      fieldKey: 'ethnicity',
      label: 'ETHNICITY',
      options: ethnicityOptions,
      disabled: true,
      xs: 3,
    },
    {
      component: SelectMultipleField,
      fieldKey: 'diagnosis',
      label: 'DIAGNOSIS',
      options: diagnosesOptions,
      required: true,
      xs: 6,
    },
  ];

  const guardianFormFields = [
    {
      component: TextDivider,
      label: 'Guardian Details',
      fieldKey: 'guardianDetails-text',
      xs: 12,
      gridItemCss: { marginTop: '10px' },
    },
    {
      component: InputField,
      label: 'FIRST NAME',
      fieldKey: 'guardianfirstName',
      required: true,
      xs: 6,
    },
    {
      component: InputField,
      label: 'LAST NAME',
      fieldKey: 'guardianlastName',
      required: true,
      xs: 6,
    },
    {
      component: InputField,
      label: 'PHONE NUMBER',
      fieldKey: 'guardianphoneNumber',
      type: 'number',
      mask: '(000) 000-0000',
      required: true,
      xs: 6,
    },
    {
      component: InputField,
      label: 'EMAIL ADDRESS',
      fieldKey: 'guardianemail',
      required: true,
      type: 'email',
      disabled: true,
      xs: 6,
    },
  ];

  const therapistFormField = [
    {
      component: SelectMultipleField,
      fieldKey: 'therapistIds',
      label: 'THERAPIST',
      options: therapistOptions,
      required: true,
      xs: 6,
    },
  ];
  const formFields = isTherapistOrGuardian
    ? [...generalFormFields, ...guardianFormFields]
    : [...generalFormFields, ...therapistFormField, ...guardianFormFields];

  return !isLoading ? (
    <FormBuilder
      backLabel="Cancel"
      disabled={disableAll}
      initValues={formValue}
      formFields={formFields}
      onBack={onBack}
      onSubmit={() => onSubmit(updatedPatient, updatedGuardian)}
      onUpdate={updateForm}
    />
  ) : null;
};
